import * as React from 'react';
import { useAmplitudeTrack } from "../../../scripts/amplitude_utils";
import {
    Box,
    Chip,
    CircularProgress,
    Divider,
    IconButton,
    InputAdornment,
    MenuItem,
    Pagination,
    Popover,
    Select,
    Stack,
    Tab,
    Tabs,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
    Tooltip,
    Typography,
    Button,
    Alert,
} from '@mui/material';
import { DateRange } from 'react-date-range';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import NotificationsOffIcon from '@mui/icons-material/NotificationsOff';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import SearchIcon from '@mui/icons-material/Search';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import AlertIcon from '../../../components/alerting/alert_icon';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import useBackend from '../../../hooks/use_backend';
import { useAllowedFeatures } from '../../../hooks/use_backend';
import Privileges from '../../../enums/privilege_enum';
import { useDevice, useDevices } from '../../../hooks/use_site';
import { fetchSiteAlerts, fetchSiteAlertsHistorical, fetchActiveSiteAlertMutes, disableSiteAlertMute } from '../../../scripts/api_v2/alerting_client';
import { AlertColors, AlertLevelClass, levelToClass } from '../../../components/alerting/alert_enums';
import { REACT_QUERY_KEYS } from '../../../enums/react_query_key_enum';
import { useMuteAlertDialog } from '../../../components/dialogs/mute_alert_dialog';
import { useMuteOverviewDialog } from '../../../components/dialogs/mute_overview_dialog';
import Snackbar from '../../../scripts/snackbar';
import 'react-date-range/dist/styles.css';
import 'react-date-range/dist/theme/default.css';
import { Error as ErrorIcon } from '@mui/icons-material';

// -------------------------------------------------------------------------------------------------
// UTILS
// -------------------------------------------------------------------------------------------------

function levelColor(level) {
    const cls = levelToClass(level);
    if (cls <= AlertLevelClass.DEBUG)    return AlertColors.DEBUG;
    if (cls === AlertLevelClass.LOG)     return AlertColors.LOG;
    if (cls === AlertLevelClass.INFO)    return AlertColors.INFO;
    if (cls === AlertLevelClass.WARNING) return AlertColors.WARNING;
    if (cls === AlertLevelClass.ERROR)   return AlertColors.ERROR;
    return AlertColors.CRITICAL;
}

function fmtDatetime(unixSec) {
    if (!unixSec) return '–';
    return new Date(unixSec * 1000).toLocaleString('en-GB', {
        year: 'numeric', month: '2-digit', day: '2-digit',
        hour: '2-digit', minute: '2-digit',
    });
}

function startOfDay(date) {
    const d = new Date(date);
    d.setHours(0, 0, 0, 0);
    return d;
}

function endOfDay(date) {
    const d = new Date(date);
    d.setHours(23, 59, 59, 999);
    return d;
}

function durationLabel(fromSec, toSec) {
    const diffSec = (toSec ?? Math.floor(Date.now() / 1000)) - fromSec;
    const m = Math.floor(diffSec / 60);
    if (m < 60) return `${m} min`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h} h ${m % 60} min`;
    return `${Math.floor(h / 24)} d ${h % 24} h`;
}


const PAGE_SIZE = 10;

const LEVEL_FILTERS = [
    { key: 'error', label: 'Error', color: AlertColors.ERROR },
    { key: 'debug', label: 'Debug', color: AlertColors.DEBUG },
];

function matchesLevelFilter(alert, activeFilters) {
    const cls = levelToClass(alert.alert_level);
    if (activeFilters.includes('error') && cls >= AlertLevelClass.ERROR) return true;
    if (activeFilters.includes('debug') && cls <= AlertLevelClass.DEBUG) return true;
    return false;
}

const cardSx = {
    borderRadius: 3,
    border: '1px solid rgba(0,0,0,0.12)',
    padding: 2,
    backgroundColor: 'white',
};

// -------------------------------------------------------------------------------------------------
// FILTER COMPONENTS
// -------------------------------------------------------------------------------------------------

function LevelFilterBar({ value, onChange, counts = {} }) {
    const toggle = (key) =>
        onChange(value.includes(key) ? value.filter((v) => v !== key) : [...value, key]);

    return (
        <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
            {LEVEL_FILTERS.map(({ key, label, color }) => {
                const active = value.includes(key);
                const count = counts[key];
                return (
                    <Chip
                        key={key}
                        label={count !== undefined ? `${label}: ${count}` : label}
                        size="small"
                        onClick={() => toggle(key)}
                        variant={active ? 'filled' : 'outlined'}
                        sx={{
                            cursor: 'pointer',
                            px: 0.75,
                            fontWeight: active ? 700 : 400,
                            backgroundColor: active ? color + '22' : 'transparent',
                            color: active ? color : 'text.secondary',
                            borderColor: active ? color + '66' : undefined,
                        }}
                    />
                );
            })}
        </Stack>
    );
}

function TagFilterBar({ allTags, value, onChange }) {
    const toggle = (tag) =>
        onChange(value.includes(tag) ? value.filter((t) => t !== tag) : [...value, tag]);

    return (
        <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
            {allTags.map((tag) => {
                const active = value.includes(tag);
                return (
                    <Chip
                        key={tag}
                        label={tag}
                        size="small"
                        icon={active
                            ? <CheckCircleOutlineIcon sx={{ fontSize: '14px !important' }} />
                            : <RadioButtonUncheckedIcon sx={{ fontSize: '14px !important', opacity: 0.35 }} />}
                        onClick={() => toggle(tag)}
                        variant={active ? 'filled' : 'outlined'}
                        sx={{
                            cursor: 'pointer',
                            px: 0.75,
                            fontWeight: active ? 700 : 400,
                            backgroundColor: active ? 'rgba(0,0,0,0.08)' : 'transparent',
                            textTransform: 'capitalize',
                        }}
                    />
                );
            })}
        </Stack>
    );
}

function SortToggle({ value, onChange }) {
    const isDate     = value === 'desc' || value === 'asc';
    const isDuration = value === 'duration_desc' || value === 'duration_asc';

    const toggleDate     = () => onChange(value === 'desc'          ? 'asc'           : 'desc');
    const toggleDuration = () => onChange(value === 'duration_desc' ? 'duration_asc'  : 'duration_desc');

    const DateArrow     = (isDate && value === 'asc')          ? ArrowUpwardIcon : ArrowDownwardIcon;
    const DurationArrow = (isDuration && value === 'duration_asc') ? ArrowUpwardIcon : ArrowDownwardIcon;

    return (
        <Stack direction="row" spacing={0.5} alignItems="center">
            <Typography variant="caption" color="text.secondary">Sort by</Typography>
            <ToggleButton
                size="small"
                value="date"
                selected={isDate}
                onClick={toggleDate}
                sx={{ fontSize: 11, px: 1.5, gap: 0.5 }}
            >
                <DateArrow sx={{ fontSize: 14 }} />
                Date
            </ToggleButton>
            <ToggleButton
                size="small"
                value="duration"
                selected={isDuration}
                onClick={toggleDuration}
                sx={{ fontSize: 11, px: 1.5, gap: 0.5 }}
            >
                <DurationArrow sx={{ fontSize: 14 }} />
                Duration
            </ToggleButton>
        </Stack>
    );
}


function DeviceFilterSelect({ allDeviceIds, value, onChange }) {
    const devices = useDevices();

    return (
        <Select
            size="small"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            displayEmpty
            sx={{ minWidth: 220, fontSize: 12 }}
        >
            <MenuItem value="">All</MenuItem>
            {allDeviceIds.map((id) => {
                const name = devices?.[id]?.name;
                return (
                    <MenuItem key={id} value={id} sx={{ fontSize: 12 }}>
                        <Stack direction="row" spacing={1} alignItems="center">
                            <Typography variant="caption" fontWeight={700}>{id}</Typography>
                            {name && <Typography variant="caption" color="text.secondary">{name}</Typography>}
                        </Stack>
                    </MenuItem>
                );
            })}
        </Select>
    );
}


function FilterBar({ allTags, allDeviceIds, levelFilter, onLevelChange, levelCounts, tagFilter, onTagChange, deviceFilter, onDeviceChange, sortOrder, onSortChange, search, onSearch }) {
    const allowedFeatures = useAllowedFeatures();
    const isAdmin = allowedFeatures.includes(Privileges.ADMIN_OVERVIEW.feature);
    return (
        <Box sx={{ ...cardSx, p: 2 }}>
            <Stack spacing={1.5}>
                <TextField
                    size="small"
                    placeholder="Search alerts…"
                    value={search}
                    onChange={(e) => onSearch(e.target.value)}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                            </InputAdornment>
                        ),
                    }}
                    sx={{ maxWidth: 360 }}
                />
                <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    justifyContent="space-between"
                    alignItems={{ xs: 'flex-start', sm: 'center' }}
                    spacing={1}
                    flexWrap="wrap"
                >
                    {isAdmin && (
                        <Stack direction="row" alignItems="center" spacing={1}>
                            <Typography variant="caption" color="text.secondary" sx={{ minWidth: 50 }}>
                                Severity
                            </Typography>
                            <LevelFilterBar value={levelFilter} onChange={onLevelChange} counts={levelCounts} />
                        </Stack>
                    )}
                    <SortToggle value={sortOrder} onChange={onSortChange} />
                </Stack>
                <Stack direction="row" alignItems="center" spacing={1}>
                    <Typography variant="caption" color="text.secondary" sx={{ minWidth: 50 }}>
                        Device
                    </Typography>
                    <DeviceFilterSelect allDeviceIds={allDeviceIds} value={deviceFilter} onChange={onDeviceChange} />
                </Stack>
                <Stack direction="row" alignItems="center" spacing={1}>
                    <Typography variant="caption" color="text.secondary" sx={{ minWidth: 50 }}>
                        Tag
                    </Typography>
                    <TagFilterBar allTags={allTags} value={tagFilter} onChange={onTagChange} />
                </Stack>
            </Stack>
        </Box>
    );
}


function DateRangePicker({ range, setRange }) {
    const [anchor, setAnchor] = React.useState(null);

    return (
        <Box>
            <Button
                startIcon={<CalendarMonthIcon />}
                onClick={(e) => setAnchor(e.currentTarget)}
                sx={{ fontWeight: 700, fontSize: 15 }}
            >
                {range.startDate.toLocaleDateString('en-GB')} – {range.endDate.toLocaleDateString('en-GB')}
            </Button>
            <Popover
                open={Boolean(anchor)}
                anchorEl={anchor}
                onClose={() => setAnchor(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            >
                <DateRange
                    ranges={[range]}
                    maxDate={new Date()}
                    onChange={(r) => setRange({ ...r.selection })}
                    color="#FF1A3D"
                    rangeColors={['#FF1A3D']}
                />
            </Popover>
        </Box>
    );
}

// -------------------------------------------------------------------------------------------------
// ALERT ROW
// -------------------------------------------------------------------------------------------------

function ActiveAlertRow({ alert, onMute, onDeviceClick }) {
    const color = levelColor(alert.alert_level);
    const device = useDevice(alert.device_id);
    return (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'stretch',
                px: 1,
                py: 1,
                borderLeft: `3px solid ${color}`,
                ml: 0.5,
                '&:hover': { backgroundColor: 'rgba(0,0,0,0.02)' },
            }}
        >
            <Box sx={{ minWidth: 28, mt: '3px', flexShrink: 0 }}>
                <AlertIcon level={alert.alert_level} icon={alert.icon} style={{ fontSize: 20 }} />
            </Box>

            <Stack sx={{ flexGrow: 1, ml: 1.5, minWidth: 0 }}>
                <Stack direction="row" spacing={1} alignItems="center">
                    <Typography variant="body2" fontWeight={600} noWrap>
                        {alert.info}
                    </Typography>
                    <Chip
                        label={alert.tag}
                        size="small"
                        sx={{ height: 16, fontSize: 10, flexShrink: 0 }}
                    />
                </Stack>
                <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap>
                    {alert.device_id && (
                        <Chip
                            label={alert.device_id}
                            size="small"
                            icon={<OpenInNewIcon sx={{ fontSize: '11px !important' }} />}
                            onClick={() => onDeviceClick && onDeviceClick(alert.device_id)}
                            sx={(theme) => ({
                                padding: 1.25,
                                height: 18,
                                fontSize: 13,
                                fontWeight: 700,
                                cursor: 'pointer',
                                backgroundColor: theme.palette.secondary.main + '1a',
                                color: theme.palette.secondary.main,
                                border: `1px solid ${theme.palette.secondary.main}59`,
                                '&:hover': { backgroundColor: theme.palette.secondary.main + '33' },
                            })}
                        />
                    )}
                    {device?.name && (
                        <Typography variant="caption" color="text.secondary" fontWeight={600}>
                            {device.name} &bull;
                        </Typography>
                    )}
                    <Typography variant="caption" color="text.secondary">
                        {alert.additional_info}
                    </Typography>
                </Stack>
            </Stack>

            <Box
                sx={{
                    flexShrink: 0,
                    ml: 2,
                    pl: 2,
                    minWidth: 190,
                    borderLeft: '1px solid rgba(0,0,0,0.07)',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                }}
            >
                <Typography variant="caption" sx={{ color: '#888', lineHeight: 1.6 }}>Active since</Typography>
                <Typography variant="caption" fontWeight={700} sx={{ color: '#555', lineHeight: 1.4 }}>
                    {fmtDatetime(alert.valid_from)}
                </Typography>
                <Typography variant="caption" sx={{ color: '#aaa', mt: 0.25 }}>
                    {durationLabel(alert.valid_from, null)} ago
                </Typography>
            </Box>

            <Tooltip title="Mute this alert">
                <IconButton
                    size="small"
                    onClick={() => onMute && onMute(alert)}
                    sx={{ ml: 1, opacity: 0.4, alignSelf: 'center', '&:hover': { opacity: 1 }, color: '#363636' }}
                >
                    <NotificationsOffIcon fontSize="small" />
                </IconButton>
            </Tooltip>
        </Box>
    );
}

function HistoryAlertRow({ alert, onDeviceClick }) {
    const color = levelColor(alert.alert_level);
    const duration = durationLabel(alert.valid_from, alert.valid_to);
    const device = useDevice(alert.device_id);
    const isActive = !alert.valid_to;

    return (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'stretch',
                px: 1,
                py: 1,
                borderLeft: `3px solid ${color}55`,
                ml: 0.5,
                '&:hover': { backgroundColor: 'rgba(0,0,0,0.02)' },
            }}
        >
            <Box sx={{ minWidth: 28, mt: '3px', flexShrink: 0, opacity: 0.75 }}>
                <AlertIcon level={alert.alert_level} icon={alert.icon} style={{ fontSize: 20 }} />
            </Box>

            <Stack sx={{ flexGrow: 1, ml: 1.5, minWidth: 0 }}>
                <Stack direction="row" spacing={1} alignItems="center">
                    <Typography variant="body2" fontWeight={600} noWrap>
                        {alert.info}
                    </Typography>
                    <Chip
                        label={alert.tag}
                        size="small"
                        sx={{ height: 16, fontSize: 10, flexShrink: 0 }}
                    />
                </Stack>
                <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap>
                    {alert.device_id && (
                        <Chip
                            label={alert.device_id}
                            size="small"
                            icon={<OpenInNewIcon sx={{ fontSize: '11px !important' }} />}
                            onClick={() => onDeviceClick && onDeviceClick(alert.device_id)}
                            sx={(theme) => ({
                                height: 18,
                                fontSize: 11,
                                fontWeight: 700,
                                cursor: 'pointer',
                                backgroundColor: theme.palette.secondary.main + '1a',
                                color: theme.palette.secondary.main,
                                border: `1px solid ${theme.palette.secondary.main}59`,
                                '&:hover': { backgroundColor: theme.palette.secondary.main + '33' },
                            })}
                        />
                    )}
                    {device?.name && (
                        <Typography variant="caption" color="text.secondary" fontWeight={600}>
                            {device.name} &bull;
                        </Typography>
                    )}
                    <Typography variant="caption" color="text.secondary">
                        {alert.additional_info}
                    </Typography>
                </Stack>
            </Stack>

            <Box
                sx={{
                    flexShrink: 0,
                    ml: 2,
                    pl: 2,
                    minWidth: 220,
                    borderLeft: '1px solid rgba(0,0,0,0.07)',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    gap: '2px',
                }}
            >
                <Stack direction="row" spacing={0.75} alignItems="baseline">
                    <Typography variant="caption" sx={{ color: '#aaa', minWidth: 28 }}>From</Typography>
                    <Typography variant="caption" fontWeight={700} sx={{ color: '#555' }}>
                        {fmtDatetime(alert.valid_from)}
                    </Typography>
                </Stack>
                <Stack direction="row" spacing={0.75} alignItems="baseline">
                    <Typography variant="caption" sx={{ color: '#aaa', minWidth: 28 }}>To</Typography>
                    <Typography variant="caption" fontWeight={700} sx={{ color: '#555' }}>
                        {fmtDatetime(alert.valid_to)}
                    </Typography>
                </Stack>
                <Chip
                    size="small"
                    label={duration}
                    sx={{
                        alignSelf: 'flex-start',
                        mt: 0.25,
                        height: 16,
                        fontSize: 10,
                        backgroundColor: color + '22',
                        color,
                        fontWeight: 700,
                        border: `1px solid ${color}44`,
                    }}
                />
            </Box>
            <Box>
                <Stack>{isActive ? <ErrorIcon color="error" fontSize="small" /> : <CheckCircleOutlineIcon color="disabled" fontSize="small" />}</Stack>
            </Box>
        </Box>
    );
}

function MutedAlertRow({ mute, onUnmute }) {
    return (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'center',
                px: 1,
                py: 1,
                borderLeft: '3px solid rgba(0,0,0,0.12)',
                ml: 0.5,
                opacity: 0.6,
                '&:hover': { backgroundColor: 'rgba(0,0,0,0.02)', opacity: 1 },
            }}
        >
            <Box sx={{ minWidth: 28, flexShrink: 0 }}>
                <NotificationsOffIcon sx={{ fontSize: 20, color: 'text.disabled' }} />
            </Box>

            <Stack sx={{ flexGrow: 1, ml: 1.5, minWidth: 0 }}>
                <Stack direction="row" spacing={1} alignItems="center">
                    <Typography variant="body2" fontWeight={600} noWrap color="text.secondary">
                        {mute.info}
                    </Typography>
                    {(mute.tag ?? mute.tag) && (
                        <Chip
                            label={mute.tag ?? mute.tag}
                            size="small"
                            sx={{ height: 16, fontSize: 10, flexShrink: 0, opacity: 0.6 }}
                        />
                    )}
                </Stack>
                <Typography variant="caption" color="text.disabled">
                    {mute.device_id && <b>{mute.device_id} &bull; </b>}
                    {mute.valid_to ? `Muted until ${fmtDatetime(Math.floor(new Date(mute.valid_to).getTime() / 1000))}` : 'Muted indefinitely'}
                    {mute.muted_by && <> &bull; by <i>{mute.muted_by}</i></>}
                </Typography>
            </Stack>

            <Tooltip title="Unmute this alert">
                <IconButton
                    size="small"
                    onClick={() => onUnmute && onUnmute(mute)}
                    sx={{ ml: 1, color: 'text.secondary', '&:hover': { color: 'primary.main' } }}
                >
                    <NotificationsActiveIcon fontSize="small" />
                </IconButton>
            </Tooltip>
        </Box>
    );
}

// ─── LoadingState / ErrorState ────────────────────────────────────────────────

function LoadingState() {
    return (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress size={32} />
        </Box>
    );
}

function ErrorState({ message }) {
    return <Alert severity="error" sx={{ mt: 1 }}>{message ?? 'Failed to load alerts.'}</Alert>;
}

// -------------------------------------------------------------------------------------------------
// ACTIVE ALERTS OVERVIEW
// -------------------------------------------------------------------------------------------------

function ActiveAlertsPanel({ siteId, backendFetch }) {
    const queryClient = useQueryClient();
    const openMuteDialog = useMuteAlertDialog();
    const openMuteOverview = useMuteOverviewDialog();
    const navigate = useNavigate();
    const { trackButtonClicked } = useAmplitudeTrack();

    const handleDeviceClick = (deviceId) => {
        trackButtonClicked('alert_open_device', 'alerts_tab_active', { site_id: siteId });
        navigate(`/sites/${siteId}/devices`, { state: { openDeviceId: deviceId } });
    };

    const { data: alerts = [], isLoading, isError } = useQuery({
        queryKey: [REACT_QUERY_KEYS.SITE_ALERTS, backendFetch, siteId],
        queryFn: () => fetchSiteAlerts(backendFetch, siteId),
        enabled: !!siteId,
        refetchInterval: 60000,
    });

    const { data: mutedAlerts = [] } = useQuery({
        queryKey: [REACT_QUERY_KEYS.ALERT_MUTES, backendFetch, siteId],
        queryFn: () => fetchActiveSiteAlertMutes(backendFetch, siteId),
        enabled: !!siteId,
        refetchInterval: 60000,
    });

    const unmuteMutation = useMutation({
        mutationFn: (muteId) => disableSiteAlertMute(backendFetch, muteId),
        onSuccess: async () => {
            await queryClient.invalidateQueries([REACT_QUERY_KEYS.SITE_ALERTS, backendFetch, siteId]);
            await queryClient.invalidateQueries([REACT_QUERY_KEYS.ALERT_MUTES, backendFetch, siteId]);
            Snackbar.success('Alert unmuted successfully');
        },
        onError: (error) => Snackbar.error('Failed to unmute alert: ' + error.message),
    });

    const handleMuteSuccess = async () => {
        await queryClient.invalidateQueries([REACT_QUERY_KEYS.SITE_ALERTS, backendFetch, siteId]);
        await queryClient.invalidateQueries([REACT_QUERY_KEYS.ALERT_MUTES, backendFetch, siteId]);
    };

    const handleUnmute = (mutedAlert) => unmuteMutation.mutateAsync(mutedAlert.id);

    const handleMuteClick = (alert) =>
        openMuteDialog(alert, mutedAlerts, handleMuteSuccess, handleUnmute);

    const allTags = React.useMemo(
        () => [...new Set(alerts.map((a) => a.tag))].sort(),
        [alerts]
    );

    const allDeviceIds = React.useMemo(
        () => [...new Set(alerts.map((a) => a.device_id).filter(Boolean))].sort(),
        [alerts]
    );

    const [levelFilter, setLevelFilter] = React.useState(['error']);
    const [tagFilter, setTagFilter]     = React.useState([]);
    const [deviceFilter, setDeviceFilter] = React.useState('');
    const [sortOrder, setSortOrder]     = React.useState('desc');
    const [search, setSearch]           = React.useState('');
    const [showMuted, setShowMuted]     = React.useState(true);
    const [page, setPage]               = React.useState(1);

    // Sync tag filter whenever allTags changes (e.g. on first load)
    React.useEffect(() => {
        setTagFilter(allTags);
    }, [allTags.join(',')]);

    // Reset to first page when filters change
    React.useEffect(() => { setPage(1); }, [levelFilter, tagFilter, deviceFilter, sortOrder, search]);

    if (isLoading) return <LoadingState />;
    if (isError)   return <ErrorState />;

    const q = search.trim().toLowerCase();
    const visible = alerts
        .filter((a) =>
            matchesLevelFilter(a, levelFilter) &&
            tagFilter.includes(a.tag) &&
            (!deviceFilter || a.device_id === deviceFilter) &&
            (!q || [a.info, a.additional_info, a.device_id, a.tag].some((s) => s?.toLowerCase().includes(q)))
        )
        .sort((a, b) => {
            if (sortOrder === 'duration_desc' || sortOrder === 'duration_asc') {
                const now = Math.floor(Date.now() / 1000);
                const dA = (a.valid_to ?? now) - a.valid_from;
                const dB = (b.valid_to ?? now) - b.valid_from;
                return sortOrder === 'duration_desc' ? dB - dA : dA - dB;
            }
            return sortOrder === 'desc' ? b.valid_from - a.valid_from : a.valid_from - b.valid_from;
        });

    const levelCounts = alerts
        .filter((a) => !deviceFilter || a.device_id === deviceFilter)
        .reduce((acc, a) => {
        const cls = levelToClass(a.alert_level);
        const key = cls >= AlertLevelClass.ERROR ? 'error' : cls <= AlertLevelClass.DEBUG ? 'debug' : null;
        if (key) acc[key] = (acc[key] ?? 0) + 1;
        return acc;
    }, {});

    return (
        <Stack spacing={2}>

            <FilterBar
                allTags={allTags}
                allDeviceIds={allDeviceIds}
                levelFilter={levelFilter}
                onLevelChange={setLevelFilter}
                levelCounts={levelCounts}
                tagFilter={tagFilter}
                onTagChange={setTagFilter}
                deviceFilter={deviceFilter}
                onDeviceChange={setDeviceFilter}
                sortOrder={sortOrder}
                onSortChange={setSortOrder}
                search={search}
                onSearch={setSearch}
            />

            <Box sx={cardSx}>
                <Typography fontWeight={700} fontSize={18} mb={2}>
                    Active alerts ({visible.length})
                </Typography>

                {visible.length === 0 ? (
                    <Typography variant="body2" color="text.secondary" textAlign="center" py={2}>
                        No active alerts match the selected filters.
                    </Typography>
                ) : (
                    <>
                        <Stack divider={<Divider />}>
                            {visible.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE).map((a) => (
                                <ActiveAlertRow key={a.id} alert={a} onMute={handleMuteClick} onDeviceClick={handleDeviceClick} />
                            ))}
                        </Stack>
                        {visible.length > PAGE_SIZE && (
                            <Box sx={{ display: 'flex', justifyContent: 'center', pt: 2 }}>
                                <Pagination
                                    count={Math.ceil(visible.length / PAGE_SIZE)}
                                    page={page}
                                    onChange={(_, v) => setPage(v)}
                                    size="small"
                                />
                            </Box>
                        )}
                    </>
                )}

                {mutedAlerts.length > 0 && (
                    <>
                        <Divider sx={{ mt: 2, mb: 1 }} />
                        <Button
                            size="small"
                            startIcon={<NotificationsOffIcon />}
                            onClick={() => setShowMuted((v) => !v)}
                            sx={{ mb: 1, color: 'text.secondary' }}
                        >
                            Muted alerts ({mutedAlerts.length})
                        </Button>
                        {showMuted && (
                            <Stack divider={<Divider />}>
                                {mutedAlerts.map((m) => (
                                    <MutedAlertRow key={m.id} mute={m} onUnmute={() => openMuteOverview(mutedAlerts, handleUnmute)} />
                                ))}
                            </Stack>
                        )}
                    </>
                )}
            </Box>
        </Stack>
    );
}

// -------------------------------------------------------------------------------------------------
// HISTORY ALERTS OVERVIEW
// -------------------------------------------------------------------------------------------------

function HistoryPanel({ siteId, backendFetch }) {
    const navigate = useNavigate();
    const { trackButtonClicked } = useAmplitudeTrack();
    const handleDeviceClick = (deviceId) => {
        trackButtonClicked('alert_open_device', 'alerts_tab_history', { site_id: siteId });
        navigate(`/sites/${siteId}/devices`, { state: { openDeviceId: deviceId } });
    };

    const [range, setRange] = React.useState({
        startDate: new Date(Date.now() - 7 * 24 * 3600 * 1000),
        endDate: new Date(),
        key: 'selection',
    });

    const fromDate = React.useMemo(() => startOfDay(range.startDate), [range.startDate]);
    const toDate = React.useMemo(() => endOfDay(range.endDate), [range.endDate]);

    const { data: alerts = [], isLoading, isError } = useQuery({
        queryKey: [REACT_QUERY_KEYS.SITE_ALERTS_HISTORICAL, backendFetch, siteId, fromDate.getTime(), toDate.getTime()],
        queryFn: () => fetchSiteAlertsHistorical(backendFetch, siteId, fromDate, toDate),
        enabled: !!siteId,
    });

    const allTags = React.useMemo(
        () => [...new Set(alerts.map((a) => a.tag))].sort(),
        [alerts]
    );

    const allDeviceIds = React.useMemo(
        () => [...new Set(alerts.map((a) => a.device_id).filter(Boolean))].sort(),
        [alerts]
    );

    const [levelFilter, setLevelFilter] = React.useState(['error']);
    const [tagFilter, setTagFilter]     = React.useState([]);
    const [deviceFilter, setDeviceFilter] = React.useState('');
    const [sortOrder, setSortOrder]     = React.useState('desc');
    const [search, setSearch]           = React.useState('');
    const [page, setPage]               = React.useState(1);

    React.useEffect(() => {
        setTagFilter(allTags);
    }, [allTags.join(',')]);

    // Reset to first page when filters or date range change
    React.useEffect(() => { setPage(1); }, [levelFilter, tagFilter, deviceFilter, sortOrder, search, range]);

    const q = search.trim().toLowerCase();
    const visible = isLoading || isError ? [] : alerts
        .filter((a) =>
            matchesLevelFilter(a, levelFilter) &&
            tagFilter.includes(a.tag) &&
            (!deviceFilter || a.device_id === deviceFilter) &&
            (!q || [a.info, a.additional_info, a.device_id, a.tag].some((s) => s?.toLowerCase().includes(q)))
        )
        .sort((a, b) => {
            if (sortOrder === 'duration_desc' || sortOrder === 'duration_asc') {
                const dA = (a.valid_to ?? a.valid_from) - a.valid_from;
                const dB = (b.valid_to ?? b.valid_from) - b.valid_from;
                return sortOrder === 'duration_desc' ? dB - dA : dA - dB;
            }
            return sortOrder === 'desc' ? b.valid_from - a.valid_from : a.valid_from - b.valid_from;
        });

    const levelCounts = alerts
        .filter((a) => !deviceFilter || a.device_id === deviceFilter)
        .reduce((acc, a) => {
        const cls = levelToClass(a.alert_level);
        const key = cls >= AlertLevelClass.ERROR ? 'error' : cls <= AlertLevelClass.DEBUG ? 'debug' : null;
        if (key) acc[key] = (acc[key] ?? 0) + 1;
        return acc;
    }, {});

    return (
        <Stack spacing={2}>
            <Box sx={{ ...cardSx, p: 2 }}>
                <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    alignItems={{ xs: 'flex-start', sm: 'center' }}
                    spacing={1}
                >
                    <Typography variant="caption" color="text.secondary" sx={{ minWidth: 50 }}>
                        Period
                    </Typography>
                    <DateRangePicker range={range} setRange={setRange} />
                </Stack>
            </Box>

            {isLoading && <LoadingState />}
            {isError   && <ErrorState />}

            {!isLoading && !isError && (
                <>
                    <FilterBar
                        allTags={allTags}
                        allDeviceIds={allDeviceIds}
                        levelFilter={levelFilter}
                        onLevelChange={setLevelFilter}
                        levelCounts={levelCounts}
                        tagFilter={tagFilter}
                        onTagChange={setTagFilter}
                        deviceFilter={deviceFilter}
                        onDeviceChange={setDeviceFilter}
                        sortOrder={sortOrder}
                        onSortChange={setSortOrder}
                        search={search}
                        onSearch={setSearch}
                    />

                    <Box sx={cardSx}>
                        <Typography fontWeight={700} fontSize={18} mb={2}>
                            History ({visible.length} alert{visible.length !== 1 ? 's' : ''})
                        </Typography>

                        {visible.length === 0 ? (
                            <Typography variant="body2" color="text.secondary" textAlign="center" py={2}>
                                No alerts found for the selected period and filters.
                            </Typography>
                        ) : (
                            <>
                                <Stack divider={<Divider />}>
                                    {visible.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE).map((a) => (
                                        <HistoryAlertRow key={a.id} alert={a} onDeviceClick={handleDeviceClick} />
                                    ))}
                                </Stack>
                                {visible.length > PAGE_SIZE && (
                                    <Box sx={{ display: 'flex', justifyContent: 'center', pt: 2 }}>
                                        <Pagination
                                            count={Math.ceil(visible.length / PAGE_SIZE)}
                                            page={page}
                                            onChange={(_, v) => setPage(v)}
                                            size="small"
                                        />
                                    </Box>
                                )}
                            </>
                        )}
                    </Box>
                </>
            )}
        </Stack>
    );
}

// -------------------------------------------------------------------------------------------------
// ALERT TAB
// -------------------------------------------------------------------------------------------------

export default function AlertsTab() {
    const { site_id: siteId } = useParams();
    const backend = useBackend();
    const [subTab, setSubTab] = React.useState('active');
    const { trackButtonClicked } = useAmplitudeTrack();

    const { data: activeAlerts = [] } = useQuery({
        queryKey: [REACT_QUERY_KEYS.SITE_ALERTS, backend.fetch, siteId],
        queryFn: () => fetchSiteAlerts(backend.fetch, siteId),
        enabled: !!siteId,
        refetchInterval: 60000,
    });

    const handleTabChange = (_, newValue) => {
        setSubTab(newValue);
        trackButtonClicked(`alerts_tab_${newValue}`, 'alerts_tab', { site_id: siteId });
    };

    return (
        <Box sx={{ backgroundColor: '#F5F5F5', mx: -3, px: 3, pb: 3 }}>
            <Box
                sx={{
                    borderBottom: 1,
                    borderColor: 'divider',
                    mb: 2,
                    backgroundColor: 'white',
                    borderRadius: '12px 12px 0 0',
                    px: 2,
                }}
            >
                <Tabs value={subTab} onChange={handleTabChange}>
                    <Tab
                        label={
                            <Stack direction="row" spacing={1} alignItems="center">
                                <span>Active Alerts</span>
                                {activeAlerts.length > 0 && (
                                    <Chip
                                        size="small"
                                        label={activeAlerts.length}
                                        sx={{
                                            height: 18,
                                            fontSize: 11,
                                            backgroundColor: AlertColors.ERROR + '22',
                                            color: AlertColors.ERROR,
                                            fontWeight: 700,
                                        }}
                                    />
                                )}
                            </Stack>
                        }
                        value="active"
                    />
                    <Tab label="History" value="history" />
                </Tabs>
            </Box>

            {subTab === 'active'  && <ActiveAlertsPanel siteId={siteId} backendFetch={backend.fetch} />}
            {subTab === 'history' && <HistoryPanel       siteId={siteId} backendFetch={backend.fetch} />}
        </Box>
    );
}
